import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import routingService from '../services/routingService';
import paymentporterService from '../services/paymentporterService';
import corporateProfileService from '../services/corporateProfileService';
import OhipLovPicker from './OhipAgentTypePicker';
import AuthorizationRuleFields, { validateAuthorizationAmount } from './AuthorizationRuleFields';
import './AddCorporateProfilePage.css';

// ONE routing form for Add and Edit (AddRoutingPage / EditRoutingPage are thin wrappers that pass `mode`).
//
// The form depends on the rule's type:
//   VCC — as always: routing type, window, card authorisation rule + amount, routing code from Opera Cloud.
//   DB  — Direct Bill. The routing code IS the porter's City Ledger (Settings tab) and is shown, not chosen — the
//         server writes the City Ledger whatever is sent — and there is no card, so no authorisation fields.

const ROUTING_TYPES = [
    { value: 'Window Routing', label: 'Window Routing' },
    { value: 'Room Routing', label: 'Room Routing' },
];

const WINDOWS = ['1', '2', '3', '4', '5', '6', '7', '8'];

const EMPTY_FORM = {
    routingType: 'Window Routing',
    defaultWindow: '1',
    routingCode: '',
    description: '',
    authorizationRuleCode: '',
    authorizationAmount: '',
    active: true,
};

const RoutingSettingsPage = ({ mode }) => {
    const isEdit = mode === 'edit';
    const navigate = useNavigate();
    const { ppId, routingId } = useParams();
    const [searchParams] = useSearchParams();
    const ruleId = searchParams.get('ruleId');
    const returnStep = searchParams.get('returnStep');
    const backUrl = ruleId ? `/apps/payment-porter/${ppId}/rules/${ruleId}${returnStep ? `?step=${returnStep}` : ''}` : `/apps/payment-porter/${ppId}`;
    const [form, setForm] = useState(isEdit ? null : EMPTY_FORM);
    const [rule, setRule] = useState(null);   // { rule_type, city_ledger_code, city_ledger_description }
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const load = async () => {
            let route = null;
            if (isEdit) {
                const res = await routingService.getById(ppId, routingId);
                route = res.data?.route || null;
            }
            const rid = ruleId || route?.rule_id;
            const ruleRow = rid ? (await paymentporterService.getRule(ppId, rid)).data?.rule || null : null;
            setRule(ruleRow);
            const isDb = ruleRow?.rule_type === 'DB';
            if (route) {
                setForm({
                    routingCode:           route.code                    || '',
                    routingType:           route.type                    || 'Window Routing',
                    description:           route.description             || '',
                    defaultWindow:         route.default_window          || '1',
                    active:                route.active !== 0,
                    authorizationRuleCode: route.authorization_rule_code != null ? String(route.authorization_rule_code) : '',
                    authorizationAmount:   route.authorization_amount != null ? String(route.authorization_amount) : '',
                });
            } else if (isDb) {
                setForm({ ...EMPTY_FORM, routingCode: ruleRow.city_ledger_code || '', description: ruleRow.city_ledger_description || '' });
            }
        };
        load().catch(() => setErrors({ submit: 'Failed to load routing' }));
    }, [ppId, routingId, ruleId, isEdit]);

    const isDb = rule?.rule_type === 'DB';
    // A DB rule always shows the CURRENT City Ledger — it is what the server will store.
    const shownCode = isDb ? (rule.city_ledger_code || '') : form?.routingCode;

    const set = (field, value) => {
        setForm(f => ({ ...f, [field]: value }));
        setErrors(e => ({ ...e, [field]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (!form.routingType) e.routingType = 'Routing Type is required';
        if (!form.defaultWindow) e.defaultWindow = 'Default Window is required';
        if (isDb && !rule.city_ledger_code) e.routingCode = 'Set the City Ledger on the Settings tab first';
        if (!isDb && !form.routingCode.trim()) e.routingCode = 'Routing Code is required';
        if (!form.description.trim()) e.description = 'Routing Code Description is required';
        if (!isDb) {
            const amountErr = validateAuthorizationAmount(form.authorizationAmount);
            if (amountErr) e.authorizationAmount = amountErr;
        }
        return e;
    };

    const handleSubmit = async () => {
        const e = validate();
        if (Object.keys(e).length) { setErrors(e); return; }
        setSaving(true);
        const body = {
            code:                    shownCode,
            type:                    form.routingType || null,
            description:             form.description || null,
            default_window:          form.defaultWindow || null,
            authorization_rule_code: isDb ? null : (form.authorizationRuleCode || null),
            authorization_amount:    isDb ? null : (form.authorizationAmount !== '' ? form.authorizationAmount : null),
        };
        try {
            if (isEdit) await routingService.update(ppId, routingId, { ...body, active: form.active });
            else        await routingService.create(ppId, body, ruleId);
            navigate(backUrl);
        } catch (err) {
            setErrors({ submit: err.response?.data?.error?.message || 'Failed to save routing' });
        } finally {
            setSaving(false);
        }
    };

    if (!form) return <div className="acp-page"><div className="acp-card" style={{ color: '#6b7280' }}>Loading...</div></div>;

    return (
        <div className="acp-page">
            <div className="acp-card">
                <div className="acp-card-header">
                    <h1 className="acp-title">{isEdit ? 'Edit' : 'Add'} Routing Settings{isDb ? ' — Direct Bill' : ''}</h1>
                    {isEdit && <p className="acp-subtitle">Update routing code configuration</p>}
                </div>

                <div className="acp-divider" />

                <div className="acp-body">
                    <div className="acp-row-2">
                        <div className="acp-field">
                            <label className="acp-label">Routing Type <span className="acp-required">*</span></label>
                            <select
                                className={`acp-select${errors.routingType ? ' acp-input-error' : ''}`}
                                value={form.routingType}
                                onChange={e => set('routingType', e.target.value)}
                                autoFocus
                            >
                                {ROUTING_TYPES.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                            {errors.routingType && <span className="acp-error-msg">{errors.routingType}</span>}
                        </div>

                        <div className="acp-field">
                            <label className="acp-label">Default Window <span className="acp-required">*</span></label>
                            <select
                                className={`acp-select${errors.defaultWindow ? ' acp-input-error' : ''}`}
                                value={form.defaultWindow}
                                onChange={e => set('defaultWindow', e.target.value)}
                            >
                                {WINDOWS.map(w => (
                                    <option key={w} value={w}>{w}</option>
                                ))}
                            </select>
                            {errors.defaultWindow && <span className="acp-error-msg">{errors.defaultWindow}</span>}
                        </div>
                    </div>

                    {/* Fixed 1-9 list + manual amount. The AMOUNT matters because pp-process-core applies
                        it to the payment method's authorizationRule at process time — without it the rule
                        replaces the reservation's existing one and the amount is lost. A Direct Bill rule
                        has no card to authorise, so it has neither. */}
                    {!isDb && (
                        <div className="acp-row-2">
                            <AuthorizationRuleFields
                                code={form.authorizationRuleCode}
                                onCodeChange={v => {
                                    set('authorizationRuleCode', v);
                                    // Clearing the code strands the amount — it would be saved but never sent.
                                    if (!v) set('authorizationAmount', '');
                                }}
                                amount={form.authorizationAmount}
                                onAmountChange={v => set('authorizationAmount', v)}
                                amountError={errors.authorizationAmount}
                            />
                        </div>
                    )}

                    <div className="acp-row-2">
                        <div className="acp-field">
                            <label className="acp-label">
                                {isDb ? 'Routing Code (City Ledger)' : 'Routing Code'} <span className="acp-required">*</span>
                            </label>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                                <input
                                    className={`acp-input${errors.routingCode ? ' acp-input-error' : ''}`}
                                    type="text"
                                    placeholder={isDb ? 'Not set — see the Settings tab' : 'Enter routing code'}
                                    value={shownCode}
                                    onChange={e => set('routingCode', e.target.value)}
                                    readOnly={isDb}
                                    title={isDb ? 'A Direct Bill rule always routes to the City Ledger set on the Settings tab' : undefined}
                                />
                                {!isDb && (
                                    <OhipLovPicker
                                        fetchFn={() => corporateProfileService.getOhipRoutingInstructions(ppId)}
                                        title="Select Routing Instruction from Opera Cloud"
                                        emptyMsg="No routing instructions returned from Opera Cloud."
                                        onSelectItem={({ code, description }) => {
                                            set('routingCode', code);
                                            if (description && description !== code) set('description', description);
                                        }}
                                    />
                                )}
                            </div>
                            {isDb && !errors.routingCode && (
                                <span className="acp-hint">Direct Bill rules always use the City Ledger set on the Settings tab.</span>
                            )}
                            {errors.routingCode && <span className="acp-error-msg">{errors.routingCode}</span>}
                        </div>

                        {isEdit && (
                            <div className="acp-field" style={{ alignSelf: 'center' }}>
                                <label className="acp-checkbox-label" style={{ marginTop: '1.5rem' }}>
                                    <input
                                        type="checkbox"
                                        className="acp-checkbox"
                                        checked={form.active}
                                        onChange={e => set('active', e.target.checked)}
                                    />
                                    Active
                                </label>
                            </div>
                        )}
                    </div>

                    <div className="acp-field">
                        <label className="acp-label">Routing Code Description <span className="acp-required">*</span></label>
                        <textarea
                            className={`acp-input${errors.description ? ' acp-input-error' : ''}`}
                            rows={4}
                            placeholder="Describe the routing code and its purpose"
                            value={form.description}
                            onChange={e => set('description', e.target.value)}
                            style={{ resize: 'vertical' }}
                        />
                        {errors.description && <span className="acp-error-msg">{errors.description}</span>}
                    </div>
                </div>

                {errors.submit && <div className="acp-error-msg" style={{ margin: '0 0 1rem' }}>{errors.submit}</div>}
                <div className="acp-footer">
                    <button className="acp-btn-cancel" onClick={() => navigate(backUrl)} disabled={saving}>
                        Cancel
                    </button>
                    <button className="acp-btn-create" onClick={handleSubmit} disabled={saving}>
                        {isEdit ? (saving ? 'Saving...' : 'Save') : (saving ? 'Creating...' : 'Create')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RoutingSettingsPage;
